import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Scissors, ArrowLeft, Mail, Key, CheckCircle, Loader2 } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // Request reset state
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetComplete, setResetComplete] = useState(false);

  // Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setRequestSent(true);
      toast.success('Controlla la tua email per il link di reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la richiesta');
    } finally {
      setLoading(false);
    }
  };

  // Reset password with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setResetComplete(true);
      toast.success('Password reimpostata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token non valido o scaduto');
    } finally {
      setLoading(false);
    }
  };

  // Reset complete view
  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle>Password Reimpostata!</CardTitle>
            <CardDescription>
              La tua password è stata cambiata con successo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Vai al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If we have a token, show reset form
  if (token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
              <Scissors className="w-8 h-8 text-primary" />
            </Link>
            <CardTitle>Nuova Password</CardTitle>
            <CardDescription>
              Inserisci la tua nuova password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nuova Password</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reimpostazione...
                  </>
                ) : (
                  'Reimposta Password'
                )}
              </Button>
            </form>
            
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-primary hover:underline">
                Torna al Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request sent confirmation
  if (requestSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <CardTitle>Controlla la tua Email</CardTitle>
            <CardDescription>
              Se l'indirizzo email è registrato, riceverai un link per reimpostare la password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Non hai ricevuto l'email? Controlla la cartella spam o{' '}
              <button 
                onClick={() => setRequestSent(false)} 
                className="text-primary hover:underline"
              >
                riprova
              </button>
            </p>
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default: request reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
            <Scissors className="w-8 h-8 text-primary" />
          </Link>
          <CardTitle>Password Dimenticata?</CardTitle>
          <CardDescription>
            Inserisci la tua email e ti invieremo un link per reimpostare la password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tuaemail@esempio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={loading} data-testid="request-reset-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                'Invia Link di Reset'
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Torna al Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
